import { clamp } from 'motion/react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react'
import type { HTMLAttributes } from 'react'

import { useMousePan } from '~/hooks/useMousePan'
import { InstanceCounterProvider, useInstanceCounter } from '~/ui'
import { cn } from '~/utils/classnames'

// ———————————————————————————
// Context
// ———————————————————————————

type SlideDeckItemState = 'active' | 'inactive' | 'in-range'

type SlideDeckControl = 'drag' | 'click'

type SlideDeckFormat = 'square' | 'portrait' | 'landscape'

type SlideDeckContextValue = {
  format: SlideDeckFormat
  control: SlideDeckControl
  activeIndex: number
  setActiveIndex: (index: number) => void
  length: number
  numVisibleItems: number
  getItemState: (index: number) => SlideDeckItemState
}

const SlideDeckContext = createContext<SlideDeckContextValue | null>(null)

export const useSlideDeckContext = () => {
  const context = useContext(SlideDeckContext)
  if (!context) {
    throw new Error('SlideDeck child component must be used within a SlideDeck')
  }
  return context
}

// ———————————————————————————————————————————————————————————
// SlideDeck | handles state and aspect ratio
// ———————————————————————————————————————————————————————————

interface SlideDeckProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  length: number
  onChange?: (index: number) => void
  control?: SlideDeckControl
  format?: SlideDeckFormat
}

export const SlideDeck = ({
  control = 'click',
  format = 'square',
  length,
  onChange,
  children,
  className,
  style,
  ...rest
}: SlideDeckProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const numVisibleItems = useMemo(() => Math.min(length, 3), [length])

  const getItemState = useCallback(
    (index: number) => {
      if (control === 'drag' || index === activeIndex) {
        return 'active'
      }

      if (
        (activeIndex === 0 && index < numVisibleItems) ||
        (activeIndex === length - 1 && index > length - 1 - numVisibleItems) ||
        index == activeIndex - 1 ||
        index === activeIndex + 1
      ) {
        return 'in-range'
      }

      return 'inactive'
    },
    [control, activeIndex, numVisibleItems, length]
  )

  const handleSetActiveIndex = useCallback(
    (index: number) => {
      onChange?.(index)
      setActiveIndex(index)
    },
    [onChange]
  )

  useEffect(() => {
    // Ensure activeIndex is within bounds
    if (activeIndex > length - 1) {
      handleSetActiveIndex(length - 1)
    }
  }, [activeIndex, handleSetActiveIndex, length])

  return (
    <SlideDeckContext.Provider
      value={{
        format,
        control,
        activeIndex,
        setActiveIndex: handleSetActiveIndex,
        length,
        numVisibleItems,
        getItemState,
      }}
    >
      <InstanceCounterProvider>
        <div
          {...rest}
          className={cn(
            'relative aspect-(--ratio) overflow-hidden rounded-md',
            '[--gap:--spacing(2.5)] [--in-range-width-hover:--spacing(16)] [--in-range-width:--spacing(10)]',
            'transition-[width,aspect-ratio] duration-500 ease-in-out',
            control === 'click' && 'pr-(--pr)',
            format === 'square' && '[--ratio:1]',
            format === 'landscape' && '[--ratio:calc(4/3)]',
            format === 'portrait' && '[--ratio:calc(3/4)]',
            className
          )}
          style={{
            '--pr': `calc(${
              numVisibleItems - 1
            } * (var(--gap) + var(--in-range-width)))`,
            ...(style || {}),
          }}
        >
          {children}
        </div>
      </InstanceCounterProvider>
    </SlideDeckContext.Provider>
  )
}

// ———————————————————————————————————————————————————————————
// useDragControl | handles drag control logic
// ———————————————————————————————————————————————————————————
const useDragControl = (isActive: boolean) => {
  const { length, activeIndex, setActiveIndex } = useSlideDeckContext()
  const { ref, cancelCurrent } = useMousePan<HTMLDivElement>(isActive)

  const currTargetIndex = useRef(-1)
  const initialScrollProgress = useRef(activeIndex / length)
  if (!isActive) initialScrollProgress.current = activeIndex / length

  useLayoutEffect(() => {
    const element = ref.current
    if (!isActive || !element) return

    const children = Array.from(element.children)

    const intersectionObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach(({ target, isIntersecting }) => {
          target.setAttribute('data-is-intersecting', isIntersecting.toString())
          if (!isIntersecting) return

          const index = children.indexOf(target)
          if (index < 0) return

          if (
            currTargetIndex.current > -1 &&
            currTargetIndex.current !== index
          ) {
            return
          } else {
            currTargetIndex.current = -1
            setActiveIndex(index)
          }
        }),
      { threshold: 0.55 }
    )

    children.forEach((child) => intersectionObserver.observe(child))

    return () => {
      children.forEach((child) => child.removeAttribute('data-is-intersecting'))
      intersectionObserver.disconnect()
    }
  }, [isActive, ref, setActiveIndex, length])

  useEffect(() => {
    const element = ref.current
    if (!isActive || !element) return

    const item = element.children[activeIndex]
    if (!item) return
    if (item.getAttribute('data-is-intersecting') === 'true') return // break if the item is already intersecting

    currTargetIndex.current = activeIndex

    cancelCurrent()
    element.scrollTo({
      left: element.scrollWidth * (activeIndex / length),
      behavior: 'smooth',
    })
  }, [activeIndex, length, ref, isActive, cancelCurrent])

  // set initial scroll position
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    if (isActive) {
      element.scrollLeft = element.scrollWidth * initialScrollProgress.current
    } else {
      element.scrollLeft = 0
    }
  }, [isActive, ref])

  return ref
}

// ———————————————————————————————————————————————————————————
// SlideDeckContent | handles item placement and rendering
// ———————————————————————————————————————————————————————————

export const SlideDeckContent = ({
  children,
  className,
  style,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => {
  const { control, length, activeIndex, numVisibleItems } =
    useSlideDeckContext()

  const ref = useDragControl(control === 'drag')

  // number of inactive items on the left and right when control is 'click'
  const [inactiveLeftItems, inactiveRightItems] = useMemo(() => {
    return [
      clamp(0, length - numVisibleItems, activeIndex - 1),
      clamp(
        0,
        length - numVisibleItems,
        length - activeIndex - numVisibleItems + 1
      ),
    ]
  }, [activeIndex, length, numVisibleItems])

  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        'absolute inset-0 h-full w-full',
        control === 'click' && [
          'flex gap-(--gap) overflow-hidden',
          '**:data-[item-order="first"]:ml-[calc(-1*var(--left,0)*var(--gap))]',
          '**:data-[item-order="last"]:mr-[calc(-1*var(--right,0)*var(--gap))]',
        ],
        control === 'drag' &&
          'hide-scrollbar grid w-full touch-manipulation snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain',
        control === 'click' && 'transform-none!',
        className
      )}
      style={{
        gridTemplateColumns:
          control === 'drag' ? `repeat(${length}, 100%)` : undefined,
        '--left': inactiveLeftItems,
        '--right': inactiveRightItems,
        ...(style || {}),
      }}
    >
      {children}
    </div>
  )
}

// ———————————————————————————————————————————————————————————
// SlideDeckItem | renders item
// ———————————————————————————————————————————————————————————

export const SlideDeckItem = ({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => {
  const { control, length, setActiveIndex, getItemState } =
    useSlideDeckContext()
  const index = useInstanceCounter()
  const state = getItemState(index)

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // block transitions when switching modes to avoid weird lingering transition when clicking
    if (control === 'click') {
      element.style.transition = 'none'
      setTimeout(() => element.style.removeProperty('transition'), 256)
    }

    return () => {
      element.style.removeProperty('transition')
    }
  }, [control])

  const handleClick = useCallback(() => {
    if (control === 'click' && state !== 'active') {
      setActiveIndex(index)
    }
  }, [control, state, setActiveIndex, index])

  return (
    <div
      ref={ref}
      {...rest}
      role={control === 'click' ? 'button' : undefined}
      className={cn(
        'h-full overflow-hidden transition-all duration-500 ease-in-out',
        control === 'drag' &&
          'w-full cursor-grab snap-center transition-none active:cursor-grabbing',
        control === 'click' && [
          'shrink rounded-md',
          state === 'active' && 'w-[calc(100%-var(--pr))]',
          state === 'inactive' && 'pointer-events-none w-0',
          state === 'in-range' &&
            'pointer:hover:w-(--in-range-width-hover) w-(--in-range-width) animate-[inert-transition_550ms_linear]',
        ],
        className
      )}
      onClick={handleClick}
      tabIndex={state === 'inactive' ? -1 : 0}
      data-item-order={
        index === 0 ? 'first' : index === length - 1 ? 'last' : 'middle'
      }
    >
      {/* This wrapper div maintains the aspect ratio defined by --ratio CSS variable, regardless of parent container dimensions */}
      <div
        className={cn(
          'relative h-full w-full',
          control === 'click' &&
            'aspect-(--ratio) w-fit transition-all duration-500 ease-in-out'
        )}
      >
        {children}
      </div>
    </div>
  )
}

// ———————————————————————————————————————————————————————————
// SlideDeckStepper | renders stepper
// ———————————————————————————————————————————————————————————

export const SlideDeckStepper = ({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => {
  const { activeIndex, length, setActiveIndex } = useSlideDeckContext()

  // don't show stepper if there are less than 2 values
  if (length < 2) return null

  return (
    <div
      {...rest}
      className={cn('flex w-full items-center justify-center', className)}
    >
      {Array.from({ length }).map((_, index) => {
        return (
          <div
            role="button"
            key={index}
            className="px-1 py-1.5"
            onClick={() => setActiveIndex(index)}
          >
            <div
              className={cn(
                'bg-highlight h-1.5 w-full rounded-full',
                'transition-width duration-300',
                index > activeIndex && 'opacity-32',
                index === activeIndex ? 'w-7' : 'w-1.5'
              )}
            />
          </div>
        )
      })}
    </div>
  )
}
